import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { ButtonLink } from "./ui";

/**
 * The masthead every inner page opens with. Shared so that /consulting and
 * /about cannot drift into looking like two different websites, which is
 * what happens when each page invents its own header.
 */
export function PageHero({
  eyebrow,
  title,
  lede,
  primary,
  secondary,
  note,
  aside,
}: {
  eyebrow: string;
  title: ReactNode;
  lede: ReactNode;
  primary?: { href: string; label: string };
  secondary?: { href: string; label: string };
  /** A quieter third action under the buttons — e.g. "already a customer". */
  note?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="relative pt-14 sm:pt-20 lg:pt-24">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[360px] w-[760px] -translate-x-1/2 rounded-full bg-brand-emerald/10 blur-[130px]" />

      <div className="container">
        <div
          className={
            aside
              ? "grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14"
              : "max-w-3xl"
          }
        >
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-white/60"
            >
              {eyebrow}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 font-display text-[2.15rem] font-bold leading-[1.06] tracking-tight sm:text-[3rem] lg:text-[3.25rem]"
            >
              {title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-5 max-w-2xl text-[15px] leading-relaxed text-white/70 sm:text-lg"
            >
              {lede}
            </motion.p>

            {primary || secondary ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
              >
                {primary ? (
                  <ButtonLink href={primary.href}>{primary.label}</ButtonLink>
                ) : null}
                {secondary ? (
                  <ButtonLink href={secondary.href} variant="ghost" icon={false}>
                    {secondary.label}
                  </ButtonLink>
                ) : null}
              </motion.div>
            ) : null}

            {note ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-5 text-xs text-white/45"
              >
                {note}
              </motion.div>
            ) : null}
          </div>

          {aside ? (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.15 }}
            >
              {aside}
            </motion.div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
